import React, { useState, useCallback } from 'react';
import { Mail, Lock, ShieldCheck } from 'lucide-react';
import logoCorge from '../../assets/logos/logo-corgee.png';
import { config } from '../../config/env';
import { tenantRegistrationService } from '../../services/tenantRegistrationService';

interface RegistrarLoginFormProps {
  onSuccess: (user: string, hasApiSession: boolean) => void;
}

/**
 * Login gate for the tenant registration console.
 * Credentials are the fixed registrar account configured in .env.
 */
export const RegistrarLoginForm: React.FC<RegistrarLoginFormProps> = ({ onSuccess }) =>
{
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const configured = Boolean(config.tenantRegistration.user && config.tenantRegistration.password);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) =>
    {
      e.preventDefault();
      setError('');

      if (!user.trim() || !password)
      {
        setError('Please enter the registrar user and password.');
        return;
      }

      if (!tenantRegistrationService.validateRegistrarCredentials(user, password))
      {
        setError('Invalid registrar credentials.');
        return;
      }

      setLoading(true);
      // API session is optional: it only enables the server side tenant list
      const token = await tenantRegistrationService.authenticateRegistrar(user, password);
      setLoading(false);
      onSuccess(user.trim(), Boolean(token));
    },
    [user, password, onSuccess],
  );

  return (
    <div className="min-h-screen bg-[#ededed] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md relative pt-20 mt-8">
        <div className="absolute -top-20 left-1/2 transform -translate-x-1/2">
          <div className="w-36 h-36 bg-white rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
            <img src={logoCorge} alt="Corgee Logo" className="w-28 h-28 object-contain" />
          </div>
        </div>

        <div className="p-6 pb-8">
          <div className="flex items-center justify-center gap-2 mb-1">
            <ShieldCheck className="w-6 h-6 text-[#62accb]" />
            <h2 className="text-2xl text-center text-[#62accb] font-normal">Tenant Registration</h2>
          </div>
          <p className="text-center text-gray-500 text-sm mb-6 font-light">
            Sign in with the registrar administrator account
          </p>

          {!configured && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              The registrar account is not configured. Set{' '}
              <code className="font-mono text-xs">VITE_TENANT_REGISTRATION_USER</code> and{' '}
              <code className="font-mono text-xs">VITE_TENANT_REGISTRATION_PASSWORD</code> in the
              .env file.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="registrarUser"
                autoComplete="username"
                placeholder="Registrar user"
                value={user}
                onChange={e =>
                {
                  setUser(e.target.value);
                  setError('');
                }}
                disabled={!configured || loading}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent disabled:bg-gray-100"
                autoFocus
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="password"
                name="registrarPassword"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={e =>
                {
                  setPassword(e.target.value);
                  setError('');
                }}
                disabled={!configured || loading}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button
              type="submit"
              disabled={!configured || loading}
              className="w-full py-2.5 bg-[#62accb] hover:bg-[#4a9bd1] text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-gray-400">
            Corgee © CGBOT {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </div>
  );
};
